<template>

  <div class="card">
    <div class="vflex">
      <div class="d-flex justify-content-between align-items-center p-4 border-bottom">
        <h5 class="mb-0">{{ $t('Message templates') }}</h5>
        <div class="btn-row">
          <button
            type="button"
            class="btn btn-outline-primary btn-sm mr-2"
            @click="openModal()">
            <i class="fa-solid fa-plus mr-1"></i>{{ $t('Add template') }}
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="loading"
            @click="refresh()">
            <i class="fa-solid fa-rotate mr-1"></i>{{ $t('Refresh') }}
          </button>
        </div>
      </div>

      <Paginaty
        :total="paginate.total"
        :pages="paginate.pages"
        :page="paginate.page"
        :creating="creating"
        :loading="loading"
        @changePage="loadData">

        <!-- list -->
        <template slot="content">
          <ul class="list-group list-group-flush">
            <li
              v-for="template in templates"
              :key="template.id || template.name"
              class="list-group-item">
              <div class="d-flex justify-content-between align-items-center">
                <strong>{{ template.name }}</strong>
                <div class="d-flex align-items-center">
                  <span
                    class="badge mr-3"
                    :class="statusClass(template.status)">
                    {{ template.status }}
                  </span>
                  <button
                    type="button"
                    class="btn btn-link link-discreet p-0"
                    :title="$t('Remove')"
                    @click="remove(template)">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
              <div class="text-muted small mb-2">
                {{ template.category }} · {{ template.language }}
              </div>
              <div
                v-for="(component, index) in template.components"
                :key="index"
                class="small">
                <span class="text-muted">{{ component.type }}:</span>
                {{ component.text }}
              </div>
            </li>
          </ul>
        </template>

        <template slot="noresults">
          {{ $t('There are no items') }}
        </template>

      </Paginaty>

      <!-- create modal -->
      <modalo
        v-if="showModal"
        :title="$t('New template')"
        type="mini"
        @hideModal="closeModal()">

        <form
          :class="{'box-loading': saving}"
          @submit.prevent="save()">
          <div class="form-group">
            <label>{{ $t('Name') }}</label>
            <input
              v-autofocus
              v-model="form.name"
              type="text"
              class="form-control"
              placeholder="confirmacao_agendamento"
              :disabled="saving">
            <ValidateMessage :error="validateErrors['name']" />
          </div>

          <div class="form-group">
            <label>{{ $t('Language') }}</label>
            <select
              v-model="form.language"
              class="form-control"
              :disabled="saving">
              <option
                v-for="language in languages"
                :key="language"
                :value="language">{{ language }}</option>
            </select>
            <ValidateMessage :error="validateErrors['language']" />
          </div>

          <div class="form-group">
            <label>{{ $t('Category') }}</label>
            <select
              v-model="form.category"
              class="form-control"
              :disabled="saving">
              <option
                v-for="category in categories"
                :key="category"
                :value="category">{{ category }}</option>
            </select>
            <ValidateMessage :error="validateErrors['category']" />
          </div>

          <div class="form-group">
            <label>{{ $t('Body') }}</label>
            <textarea
              v-model="form.body"
              class="form-control"
              rows="3"
              :placeholder="bodyPlaceholder"
              :disabled="saving"></textarea>
            <small class="text-muted">{{ $t('whatsapp_template_body_help') }}</small>
            <ValidateMessage :error="validateErrors['components']" />
          </div>

          <!-- example values for placeholders -->
          <div
            v-if="placeholders.length"
            class="form-group">
            <label>{{ $t('Example values') }}</label>
            <div
              v-for="placeholder in placeholders"
              :key="placeholder"
              class="input-group mb-2">
              <div class="input-group-prepend">
                <span class="input-group-text">{{ wrapPlaceholder(placeholder) }}</span>
              </div>
              <input
                v-model="examples[placeholder]"
                type="text"
                class="form-control"
                :disabled="saving">
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('Footer') }} <span class="text-muted">({{ $t('Optional') }})</span></label>
            <input
              v-model="form.footer"
              type="text"
              class="form-control"
              :disabled="saving">
          </div>

          <div
            v-if="errorMessage"
            class="alert alert-warning">
            {{ errorMessage }}
          </div>
        </form>

        <template slot="footer">
          <button
            type="button"
            class="btn btn-light mr-2"
            :disabled="saving"
            @click="closeModal()">{{ $t('Cancel') }}</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="saving || !form.name || !form.body"
            @click="save()">{{ $t('Create template') }}</button>
        </template>

      </modalo>

    </div>
  </div>

</template>

<script>
import Paginaty from '@/components/Paginaty/Paginaty'

export default {
  name: 'WhatsappPageTemplates',
  components: {
    Paginaty
  },
  props: {
    connectionId: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      creating: true,
      loading: true,
      saving: false,
      showModal: false,
      errorMessage: null,
      validateErrors: {},
      templates: [],
      paginate: {
        total: 0,
        pages: 0,
        page: 0
      },
      languages: ['pt_BR', 'en_US', 'es_ES'],
      categories: ['UTILITY', 'MARKETING', 'AUTHENTICATION'],
      examples: {},
      form: {
        name: '',
        language: 'pt_BR',
        category: 'UTILITY',
        body: '',
        footer: ''
      }
    }
  },
  computed: {
    // literal example, kept out of the template to avoid mustache interpolation
    bodyPlaceholder () {
      return 'Olá {{1}}, seu agendamento está confirmado para {{2}}. Obrigado!'
    },
    // placeholders detected in the body text, e.g. {{1}} {{2}} or {{name}}
    placeholders () {
      const matches = this.form.body.match(/{{\s*([^}]+?)\s*}}/g) || []
      const unique = []
      matches.forEach((match) => {
        const key = match.replace(/{{\s*|\s*}}/g, '')
        if (!unique.includes(key)) {
          unique.push(key)
        }
      })
      return unique
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    /*
    openModal
    */
    openModal () {
      this.reset()
      this.errorMessage = null
      this.validateErrors = {}
      this.showModal = true
    },
    /*
    closeModal
    */
    closeModal () {
      this.showModal = false
    },
    /*
    loadData
    */
    loadData (page) {
      this.loading = true
      this.errorMessage = null

      this.$store
      .dispatch('getWhatsappTemplates', {
        id: this.connectionId,
        filters: {
          page: page,
          limit: this.$default.limit
        }
      })
      .then((result) => {
        this.templates = (result && result.docs) || []
        this.paginate = {
          total: result.total,
          pages: result.pages,
          page: result.page
        }
        if (this.templates.length === 0 && result.page > 1) this.loadData(1)
      })
      .catch((error) => {
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || this.$t('Something went wrong')
      })
      .finally(() => {
        this.loading = this.creating = false
      })
    },
    /*
    refresh
    ressincroniza os templates com a meta e recarrega a lista
    */
    refresh () {
      this.loading = true
      this.errorMessage = null

      this.$store
      .dispatch('refreshWhatsappTemplates', this.connectionId)
      .then(() => {
        this.$store.dispatch('setWarn', { status: 'success' })
        this.loadData(this.paginate.page)
      })
      .catch((error) => {
        this.loading = false
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || this.$t('Something went wrong')
      })
    },
    /*
    save
    */
    save () {
      this.saving = true
      this.errorMessage = null
      this.validateErrors = {}

      this.$store
      .dispatch('createWhatsappTemplate', {
        id: this.connectionId,
        data: this.buildPayload()
      })
      .then(() => {
        this.$store.dispatch('setWarn', { status: 'success' })
        this.closeModal()
        this.reset()
        this.loadData(this.paginate.page)
      })
      .catch((error) => {
        const data = error.response && error.response.data
        if (data && data.name === 'ValidationError') {
          this.validateErrors = data.errors
        } else {
          this.errorMessage = (data && data.message) || this.$t('Something went wrong')
        }
      })
      .finally(() => {
        this.saving = false
      })
    },
    /*
    remove
    */
    remove (template) {
      const deleteAction = () => {
        this.loading = true
        this.errorMessage = null

        this.$store
        .dispatch('deleteWhatsappTemplate', {
          id: this.connectionId,
          name: template.name
        })
        .then(() => {
          this.loadData(this.paginate.page)
        })
        .catch((error) => {
          this.loading = false
          this.errorMessage = (error.response && error.response.data && error.response.data.message) || this.$t('Something went wrong')
        })
      }

      this.$store.dispatch('setAlertModal', {
        action: deleteAction
      })
    },
    /*
    buildPayload
    */
    buildPayload () {
      const body = {
        type: 'BODY',
        text: this.form.body
      }

      const exampleValues = this.placeholders.map((placeholder) => {
        return this.examples[placeholder] || ''
      })

      if (exampleValues.length) {
        body.example = {
          body_text: [exampleValues]
        }
      }

      const components = [body]

      if (this.form.footer) {
        components.push({
          type: 'FOOTER',
          text: this.form.footer
        })
      }

      return {
        name: this.form.name,
        language: this.form.language,
        category: this.form.category,
        parameter_format: 'POSITIONAL',
        components: components
      }
    },
    /*
    reset
    */
    reset () {
      this.examples = {}
      this.form.name = ''
      this.form.body = ''
      this.form.footer = ''
    },
    /*
    wrapPlaceholder
    */
    wrapPlaceholder (placeholder) {
      return '{{' + placeholder + '}}'
    },
    /*
    statusClass
    */
    statusClass (status) {
      switch (status) {
        case 'APPROVED':
          return 'badge-success'
        case 'REJECTED':
          return 'badge-danger'
        default:
          return 'badge-secondary'
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/Integrations/connectors/whatsapp/PageTemplates.vue